import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useState } from "react";

// [AI_START::module id="useAccessToken" purpose="Hold access token for Apollo; avoid cacheMode=off on localhost which re-triggers Auth0 consent_required" links=["auth0-provider-with-navigate", "withApolloClient"]]

const TOKEN_REFRESH_INTERVAL = 24 * 60 * 60 * 1000;

const isInteractiveLoginRequired = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const err = error as { error?: string; message?: string };
  return (
    err.error === "consent_required" ||
    err.error === "login_required" ||
    err.error === "interaction_required" ||
    /consent required/i.test(err.message ?? "")
  );
};

const useAccessToken = () => {
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // [AI_START::function id="getAccessToken" purpose="Fetch token from Auth0 cache/session; redirect when silent auth needs consent/login" links=["useAccessToken"]]
  const getAccessToken = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      setAccessToken(token);
    } catch (error) {
      if (isInteractiveLoginRequired(error)) {
        await loginWithRedirect();
        return;
      }
      throw error;
    }
  }, [getAccessTokenSilently, loginWithRedirect]);
  // [AI_END::getAccessToken]

  useEffect(() => {
    getAccessToken();

    const intervalId = setInterval(() => {
      getAccessToken();
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [getAccessToken]);

  return accessToken;
};

export default useAccessToken;

// [AI_END::useAccessToken]
